/**
 * TableStyleEditor Component
 * Dialog for editing table style settings:
 * - Border style (None, Solid, Dashed)
 * - Border color
 * - Header background and text color
 * - Alternate row color
 * - Cell padding
 */

import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Palette,
  Grid3X3,
  StretchHorizontal,
  Eye,
} from 'lucide-react'
import type { TableStyle } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface TableStyleEditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  style?: TableStyle
  onStyleChange: (style: TableStyle) => void
}

// ============================================
// Constants
// ============================================

const BORDER_STYLES = [
  { value: 'None', label: 'No Border' },
  { value: 'Solid', label: 'Solid' },
  { value: 'Dashed', label: 'Dashed' },
]

const PRESET_COLORS = [
  '#1a1a1a', // Black
  '#404040', // Dark Gray
  '#737373', // Gray
  '#a3a3a3', // Light Gray
  '#e5e5e5', // Lighter Gray
  '#ffffff', // White
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
]

const PRESET_STYLES: { name: string; style: TableStyle }[] = [
  {
    name: 'Default',
    style: {
      borderStyle: 'Solid',
      borderColor: '#e5e5e5',
      headerBackground: '#f5f5f5',
      headerTextColor: '#1a1a1a',
      alternateRowColor: undefined,
      cellPadding: 12,
    },
  },
  {
    name: 'Minimal',
    style: {
      borderStyle: 'None',
      borderColor: undefined,
      headerBackground: '#ffffff',
      headerTextColor: '#737373',
      alternateRowColor: undefined,
      cellPadding: 8,
    },
  },
  {
    name: 'Striped',
    style: {
      borderStyle: 'Solid',
      borderColor: '#e5e5e5',
      headerBackground: '#1a1a1a',
      headerTextColor: '#ffffff',
      alternateRowColor: '#fafafa',
      cellPadding: 12,
    },
  },
  {
    name: 'Blue Theme',
    style: {
      borderStyle: 'Solid',
      borderColor: '#bfdbfe',
      headerBackground: '#3b82f6',
      headerTextColor: '#ffffff',
      alternateRowColor: '#eff6ff',
      cellPadding: 12,
    },
  },
  {
    name: 'Green Theme',
    style: {
      borderStyle: 'Solid',
      borderColor: '#bbf7d0',
      headerBackground: '#22c55e',
      headerTextColor: '#ffffff',
      alternateRowColor: '#f0fdf4',
      cellPadding: 12,
    },
  },
]

// ============================================
// ColorPicker Component
// ============================================

interface ColorPickerProps {
  value?: string
  onChange: (color: string | undefined) => void
  label: string
  allowClear?: boolean
}

function ColorPicker({ value, onChange, label, allowClear = true }: ColorPickerProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {allowClear && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className={cn(
              'w-7 h-7 rounded-lg border-2 transition-all',
              'flex items-center justify-center text-xs',
              !value
                ? 'border-blue-500 bg-neutral-100 dark:bg-neutral-800'
                : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
            )}
            title="No color"
          >
            <span className="text-neutral-400">-</span>
          </button>
        )}
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            className={cn(
              'w-7 h-7 rounded-lg border-2 transition-all',
              value === color
                ? 'border-blue-500 scale-110'
                : 'border-transparent hover:scale-105'
            )}
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
        <Input
          type="color"
          value={value || '#e5e5e5'}
          onChange={(e) => onChange(e.target.value)}
          className="w-7 h-7 p-0 border-2 rounded-lg cursor-pointer"
          title="Custom color"
        />
      </div>
      {value && (
        <p className="text-xs text-neutral-500 font-mono">{value}</p>
      )}
    </div>
  )
}

// ============================================
// PreviewTable Component
// ============================================

interface PreviewTableProps {
  style: TableStyle
}

function PreviewTable({ style }: PreviewTableProps) {
  const sampleData = [
    { name: 'Item A', quantity: '10', price: '$25.00' },
    { name: 'Item B', quantity: '5', price: '$50.00' },
    { name: 'Item C', quantity: '15', price: '$12.50' },
  ]

  const borderClass = style.borderStyle === 'Dashed'
    ? 'border-dashed'
    : style.borderStyle === 'None'
      ? 'border-0'
      : 'border-solid'

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className={cn('w-full text-sm', borderClass)}>
        <thead>
          <tr
            style={{
              backgroundColor: style.headerBackground || '#f5f5f5',
              borderColor: style.borderColor,
            }}
            className="border-b-2"
          >
            <th
              style={{
                color: style.headerTextColor,
                padding: `${style.cellPadding || 12}px`,
                borderColor: style.borderColor,
              }}
              className="text-left font-semibold"
            >
              Name
            </th>
            <th
              style={{
                color: style.headerTextColor,
                padding: `${style.cellPadding || 12}px`,
                borderColor: style.borderColor,
              }}
              className="text-right font-semibold"
            >
              Qty
            </th>
            <th
              style={{
                color: style.headerTextColor,
                padding: `${style.cellPadding || 12}px`,
                borderColor: style.borderColor,
              }}
              className="text-right font-semibold"
            >
              Price
            </th>
          </tr>
        </thead>
        <tbody>
          {sampleData.map((row, index) => (
            <tr
              key={index}
              style={{
                backgroundColor: style.alternateRowColor && index % 2 === 1
                  ? style.alternateRowColor
                  : undefined,
                borderColor: style.borderColor,
              }}
              className="border-b last:border-0"
            >
              <td style={{ padding: `${style.cellPadding || 12}px` }}>{row.name}</td>
              <td style={{ padding: `${style.cellPadding || 12}px` }} className="text-right font-mono">
                {row.quantity}
              </td>
              <td style={{ padding: `${style.cellPadding || 12}px` }} className="text-right font-mono">
                {row.price}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================
// TableStyleEditor Component
// ============================================

export function TableStyleEditor({
  open,
  onOpenChange,
  style,
  onStyleChange,
}: TableStyleEditorProps) {
  const [localStyle, setLocalStyle] = useState<TableStyle>(
    style || PRESET_STYLES[0]!.style
  )

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalStyle(style || PRESET_STYLES[0]!.style)
    }
  }, [open, style])

  // Apply preset
  const handleApplyPreset = (preset: TableStyle) => {
    setLocalStyle(preset)
  }

  // Apply changes
  const handleApply = () => {
    onStyleChange(localStyle)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Table Style
          </DialogTitle>
          <DialogDescription>
            Customize the appearance of your table.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="presets" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="presets" className="gap-2">
              <Grid3X3 className="h-4 w-4" />
              Presets
            </TabsTrigger>
            <TabsTrigger value="custom" className="gap-2">
              <Palette className="h-4 w-4" />
              Custom
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto py-4">
            <TabsContent value="presets" className="m-0 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {PRESET_STYLES.map((preset) => (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => handleApplyPreset(preset.style)}
                    className={cn(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      'hover:border-blue-300 dark:hover:border-blue-700',
                      JSON.stringify(localStyle) === JSON.stringify(preset.style)
                        ? 'border-blue-500'
                        : 'border-neutral-200 dark:border-neutral-700'
                    )}
                  >
                    <p className="text-sm font-medium mb-2">{preset.name}</p>
                    <div
                      className="h-4 rounded flex overflow-hidden"
                      style={{
                        backgroundColor: preset.style.headerBackground,
                        border: preset.style.borderStyle !== 'None'
                          ? `1px ${preset.style.borderStyle?.toLowerCase()} ${preset.style.borderColor}`
                          : 'none',
                      }}
                    >
                      {preset.style.alternateRowColor && (
                        <div
                          className="w-1/2 h-full"
                          style={{ backgroundColor: preset.style.alternateRowColor }}
                        />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom" className="m-0 space-y-6">
              {/* Border Style */}
              <div className="space-y-2">
                <Label>Border Style</Label>
                <Select
                  value={localStyle.borderStyle || 'Solid'}
                  onValueChange={(value) =>
                    setLocalStyle({ ...localStyle, borderStyle: value as TableStyle['borderStyle'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BORDER_STYLES.map((style) => (
                      <SelectItem key={style.value} value={style.value}>
                        {style.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Border Color */}
              {localStyle.borderStyle !== 'None' && (
                <ColorPicker
                  label="Border Color"
                  value={localStyle.borderColor}
                  onChange={(color) => setLocalStyle({ ...localStyle, borderColor: color })}
                  allowClear={false}
                />
              )}

              {/* Header Background */}
              <ColorPicker
                label="Header Background"
                value={localStyle.headerBackground}
                onChange={(color) => setLocalStyle({ ...localStyle, headerBackground: color })}
              />

              {/* Header Text Color */}
              <ColorPicker
                label="Header Text Color"
                value={localStyle.headerTextColor}
                onChange={(color) => setLocalStyle({ ...localStyle, headerTextColor: color })}
              />

              {/* Alternate Row Color */}
              <ColorPicker
                label="Alternate Row Color"
                value={localStyle.alternateRowColor}
                onChange={(color) => setLocalStyle({ ...localStyle, alternateRowColor: color })}
              />

              {/* Cell Padding */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <StretchHorizontal className="h-4 w-4" />
                    Cell Padding
                  </Label>
                  <span className="text-sm text-neutral-500">
                    {localStyle.cellPadding || 12}px
                  </span>
                </div>
                <Slider
                  value={[localStyle.cellPadding || 12]}
                  onValueChange={([value]) => setLocalStyle({ ...localStyle, cellPadding: value })}
                  min={4}
                  max={24}
                  step={2}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Preview */}
        <div className="space-y-2 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <Label className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </Label>
          <PreviewTable style={localStyle} />
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply Style
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TableStyleEditor
